import React, { useState, useEffect } from "react";
import {
  db,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
} from "./firebaseConfig";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import UniversalTextField from "./GlobalComponents/UniversalTextField";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Modal from "@mui/material/Modal";
import Slide from "@mui/material/Slide";
import EventDataModalV2 from "./Calendar/EventDataModalV2";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import getJobName from "./GlobalFunctions/getJobName";

import { useAtom } from "jotai";
import {
  themeAtom,
  labelsAtom,
  labelColoursAtom,
  shouldPauseSubscriptionsAtom,
  listsAtom,
} from "./-Atoms";

const { DateTime } = require("luxon");

const VideoSchedule = () => {
  const [videoJobs, setVideoJobs] = useState([]);
  const [shouldPauseSubscriptions] = useAtom(shouldPauseSubscriptionsAtom);
  const [theme] = useAtom(themeAtom);
  const [eventDataModalV2Open, setEventDataModalV2Open] = useState(false);
  const [modalJobData, setModalJobData] = useState({});
  const [showHiddenJobs, setShowHiddenJobs] = useState(false);

  useEffect(() => {
    if (shouldPauseSubscriptions) {
      return;
    }

    const today = DateTime.now()
      .setZone("Australia/Sydney")
      .startOf("day")
      .plus({ days: 15 })
      .toUTC()
      .toISO();
    const q = query(
      collection(db, "JobData"),
      where("jobTypeFull", "==", "Video (-V)"),
      where("bookedStart", "<=", today),
      orderBy("bookedStart", "desc"),
      limit(40)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      let docs = [];
      snapshot.docs.forEach((doc) => {
        try {
          const data = doc.data();
          data.id = doc.id;
          docs.push(data);
        } catch (e) {
          console.log(e);
        }
      });
      setVideoJobs([...docs]);
    });

    return () => {
      unsub();
    };
  }, [shouldPauseSubscriptions]);

  const headerCellStyle = {
    backgroundColor:
      theme === "dark" ? "rgba(40, 40, 40, 0.95)" : "rgba(240, 240, 240, 0.95)",
    color:
      theme === "dark"
        ? "rgba(255, 255, 255, 0.9)"
        : "rgba(0, 0, 0, 0.9)",
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: "16px",
  };

  // Filter jobs based on hidden state
  const filteredJobs = videoJobs.filter((job) => {
    if (showHiddenJobs) return true; // Show all jobs
    return !job.videoScheduleHidden; // Only show non-hidden jobs
  });

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          overflow: "auto",
          backgroundColor:
            theme === "dark" ? "rgba(18, 18, 18, 1)" : "rgba(250, 250, 250, 1)",
        }}
      >
        {/* Toggle for showing hidden jobs */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            backgroundColor:
              theme === "dark"
                ? "rgba(28, 28, 28, 0.95)"
                : "rgba(255, 255, 255, 0.95)",
            borderBottom: `1px solid ${theme === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)"
              }`,
            padding: "12px 16px",
            display: "flex",
            justifyContent: "flex-end",
            backdropFilter: "blur(10px)",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showHiddenJobs}
                onChange={(e) => setShowHiddenJobs(e.target.checked)}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: theme === "dark" ? "#90caf9" : "#1976d2",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: theme === "dark" ? "#90caf9" : "#1976d2",
                  },
                }}
              />
            }
            label="Show Hidden Jobs"
            sx={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(0, 0, 0, 0.87)",
              "& .MuiFormControlLabel-label": {
                fontSize: "0.9rem",
                fontWeight: 500,
              },
            }}
          />
        </Box>

        <TableContainer
          sx={{
            height: "calc(100% - 49px)",
            backgroundColor:
              theme === "dark"
                ? "rgba(28, 28, 28, 1)"
                : "rgba(255, 255, 255, 1)",
          }}
        >
          <Table stickyHeader sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellStyle, width: "220px" }}>Job Name</TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "170px" }}>Status</TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "120px" }}>Shoot Date</TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "250px" }}>Labels</TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "200px" }}>Time Estimate</TableCell>
                <TableCell sx={{ ...headerCellStyle, width: "auto" }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs &&
                filteredJobs.map((job) => (
                  <VideoTableRow
                    key={job.id}
                    job={job}
                    setModalJobData={setModalJobData}
                    setEventDataModalV2Open={setEventDataModalV2Open}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Modal
        open={eventDataModalV2Open}
        onClose={() => setEventDataModalV2Open(false)}
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          paddingTop: 10,
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
        }}
      >
        <Slide
          direction="up"
          in={eventDataModalV2Open}
          mountOnEnter
          unmountOnExit
          timeout={120}
        >
          <div
            style={{
              width: "800px",
              height: "calc(100vh - 125px)",
              background:
                theme === "dark" ? "rgb(28, 28, 28)" : "rgb(250, 250, 250)",
              zIndex: 1100,
              position: "relative",
              outline: "none",
              boxShadow: "0px 3px 10px rgba(0, 0, 0, 0.2)",
              overflowY: "auto",
              borderRadius: "10px",
            }}
          >
            <div>
              <EventDataModalV2
                initialJobData={modalJobData}
                eventDataModalOpen={eventDataModalV2Open}
                setEventDataModalOpen={setEventDataModalV2Open}
              />
            </div>
          </div>
        </Slide>
      </Modal>
    </>
  );
};

const VideoTableRow = ({ job, setModalJobData, setEventDataModalV2Open }) => {
  const [theme] = useAtom(themeAtom);
  const [labels] = useAtom(labelsAtom);
  const [labelColours] = useAtom(labelColoursAtom);
  const [lists] = useAtom(listsAtom);
  const [contextMenu, setContextMenu] = useState(null);

  const isCompleted =
    job.idLabels?.includes("5e6881d6af988c41f2bf6e12") ||
    job.idLabelsPermanent?.includes("5e6881d6af988c41f2bf6e12");

  const isReadyToSupply =
    job.idLabels?.includes("618dd3056dc4c70d7d83072e") ||
    job.idLabelsPermanent?.includes("618dd3056dc4c70d7d83072e");

  // Find "Complete - To Be Invoiced" list ID
  const completeToBeInvoicedListId = lists.find(
    (list) => list.name === "Complete - To Be Invoiced"
  )?.id;

  const isInCompleteToBeInvoicedList =
    completeToBeInvoicedListId &&
    Array.isArray(lists.find(l => l.id === completeToBeInvoicedListId)?.items) &&
    lists.find(l => l.id === completeToBeInvoicedListId).items.includes(job.id);

  // Check if job is scheduled for the future (hasn't been shot yet)
  const isToBeShot = () => {
    const shootDate = DateTime.fromISO(job.bookedStart).setZone("Australia/Sydney");
    const today = DateTime.now().setZone("Australia/Sydney").startOf("day");
    return shootDate > today;
  };

  // Determine status priority: Complete To Be Invoiced > Complete > Ready to Supply > To Be Shot > In Progress
  const getStatus = () => {
    if (isInCompleteToBeInvoicedList) return "completeToBeInvoiced";
    if (job.complete) return "complete";
    if (isCompleted) return "complete";
    if (isReadyToSupply) return "readyToSupply";
    if (isToBeShot()) return "toBeShot";
    return "inProgress";
  };

  const status = getStatus();

  const handleOpenJob = () => {
    // Create event-like structure for EventDataModalV2
    const eventData = {
      id: job.id,
      extendedProps: job,
    };
    setModalJobData(eventData);
    setEventDataModalV2Open(true);
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleToggleHidden = async () => {
    try {
      await updateDoc(doc(db, "JobData", job.id), {
        videoScheduleHidden: !job.videoScheduleHidden,
      });
    } catch (error) {
      console.error("Error toggling job visibility:", error);
    }
    handleCloseContextMenu();
  };

  return (
    <>
      <TableRow
        onClick={handleOpenJob}
        onContextMenu={handleContextMenu}
        sx={{
          opacity:
            job.idLabels?.includes("5e6881d6af988c41f2bf6e12") ||
              job.idLabelsPermanent?.includes("5e6881d6af988c41f2bf6e12") ||
              !job.jobNumber
              ? 0.8
              : 1,
          background: parseBGColour(job, theme),
          transition: "all 0.2s ease",
          cursor: "pointer",
          "&:hover": {
            backgroundColor:
              theme === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.03)",
          },
        }}
      >
        {/* Job Name Column */}
        <TableCell
          sx={{
            color:
              theme === "dark"
                ? "rgba(255, 255, 255, 0.9)"
                : "rgba(0, 0, 0, 0.87)",
            padding: "12px 16px",
          }}
        >
          <Box sx={{ fontWeight: 500 }}>{getJobName(job)}</Box>
        </TableCell>

        {/* Status Column */}
        <TableCell sx={{ padding: "12px 16px" }}>
          <Chip
            label={
              status === "completeToBeInvoiced"
                ? "Complete - To Be Invoiced"
                : status === "complete"
                  ? "Complete"
                  : status === "readyToSupply"
                    ? "Ready to Supply"
                    : status === "toBeShot"
                      ? "To Be Shot"
                      : "In Progress"
            }
            size="small"
            sx={{
              width: status === "completeToBeInvoiced" ? "auto" : "140px",
              backgroundColor:
                status === "completeToBeInvoiced"
                  ? theme === "dark"
                    ? "rgba(156, 39, 176, 0.2)"
                    : "rgba(156, 39, 176, 0.25)"
                  : status === "complete"
                    ? theme === "dark"
                      ? "rgba(10, 180, 50, 0.2)"
                      : "rgba(10, 180, 50, 0.25)"
                    : status === "readyToSupply"
                      ? theme === "dark"
                        ? "rgba(59, 130, 246, 0.2)"
                        : "rgba(59, 130, 246, 0.25)"
                      : status === "toBeShot"
                        ? theme === "dark"
                          ? "rgba(128, 128, 128, 0.2)"
                          : "rgba(128, 128, 128, 0.25)"
                        : theme === "dark"
                          ? "rgba(255, 165, 0, 0.2)"
                          : "rgba(255, 165, 0, 0.25)",
              color:
                status === "completeToBeInvoiced"
                  ? theme === "dark"
                    ? "#e056fd"
                    : "#9c27b0"
                  : status === "complete"
                    ? theme === "dark"
                      ? "#4ade80"
                      : "#16a34a"
                    : status === "readyToSupply"
                      ? theme === "dark"
                        ? "#60a5fa"
                        : "#2563eb"
                      : status === "toBeShot"
                        ? theme === "dark"
                          ? "#b0b0b0"
                          : "#707070"
                        : theme === "dark"
                          ? "#fbbf24"
                          : "#d97706",
              fontWeight: 600,
              fontSize: "0.75rem",
              borderRadius: "6px",
              border: `1px solid ${status === "completeToBeInvoiced"
                  ? theme === "dark"
                    ? "rgba(156, 39, 176, 0.3)"
                    : "rgba(156, 39, 176, 0.4)"
                  : status === "complete"
                    ? theme === "dark"
                      ? "rgba(10, 180, 50, 0.3)"
                      : "rgba(10, 180, 50, 0.4)"
                    : status === "readyToSupply"
                      ? theme === "dark"
                        ? "rgba(59, 130, 246, 0.3)"
                        : "rgba(59, 130, 246, 0.4)"
                      : status === "toBeShot"
                        ? theme === "dark"
                          ? "rgba(128, 128, 128, 0.3)"
                          : "rgba(128, 128, 128, 0.4)"
                        : theme === "dark"
                          ? "rgba(255, 165, 0, 0.3)"
                          : "rgba(255, 165, 0, 0.4)"
                }`,
            }}
          />
        </TableCell>

        {/* Shoot Date Column */}
        <TableCell sx={{ padding: "12px 16px", color: theme === "dark" ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.87)" }}>
          {DateTime.fromISO(job.bookedStart).toFormat("dd-MM-yyyy")}
        </TableCell>

        {/* Labels Column */}
        <TableCell sx={{ padding: "12px 16px" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 300 }}>
            {job.idLabels?.map((labelID, idx) => (
              <LabelDiv
                key={`label-${labelID}-${idx}`}
                labelID={labelID}
                labels={labels}
                labelColours={labelColours}
                theme={theme}
              />
            ))}
          </Box>
        </TableCell>

        {/* Time Estimate Column */}
        <TableCell
          onClick={(e) => e.stopPropagation()}
          sx={{ padding: "8px 16px" }}
        >
          <Box sx={{ width: "100%" }}>
            <UniversalTextField
              collectionName="JobData"
              docId={job.id}
              fieldName="videoTimeEstimate"
              value={job.videoTimeEstimate || ""}
              label="Time Estimate"
              variant="filled"
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </TableCell>

        {/* Notes Column */}
        <TableCell
          onClick={(e) => e.stopPropagation()}
          sx={{ padding: "8px 16px" }}
        >
          <Box sx={{ width: "100%" }}>
            <UniversalTextField
              collectionName="JobData"
              docId={job.id}
              fieldName="videoNotes"
              value={job.videoNotes || ""}
              label="Notes"
              variant="filled"
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </TableCell>
      </TableRow>

      <Menu
        open={Boolean(contextMenu)}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleToggleHidden}>
          {job.videoScheduleHidden ? (
            <>
              <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
              Show Job
            </>
          ) : (
            <>
              <VisibilityOffIcon fontSize="small" sx={{ mr: 1 }} />
              Hide Job
            </>
          )}
        </MenuItem>
      </Menu>
    </>
  );
};

const LabelDiv = ({ labelID, labels, labelColours, theme }) => {
  const theLabel = labels?.find((obj) => obj.id === labelID);

  if (!theLabel || !theLabel.name?.includes("VIDEO")) {
    return null;
  }

  const baseColor = labelColours?.[theLabel.color]?.[theme];

  // Convert RGB to HSL
  const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }

    return [h * 360, s * 100, l * 100];
  };

  // Convert HSL to RGB
  const hslToRgb = (h, s, l) => {
    h /= 360;
    s /= 100;
    l /= 100;

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  };

  // Convert color to rgba format with specified opacity
  const convertToRgba = (color, opacity) => {
    if (!color) {
      return theme === "dark"
        ? `rgba(100, 100, 100, ${opacity})`
        : `rgba(100, 100, 100, ${opacity})`;
    }

    // Handle hex colors
    if (color.startsWith("#")) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // Handle rgb colors
    if (color.startsWith("rgb(")) {
      return color.replace("rgb", "rgba").replace(")", `, ${opacity})`);
    }

    // Handle rgba colors - replace the opacity
    if (color.startsWith("rgba")) {
      return color.replace(/[\d.]+\)$/g, `${opacity})`);
    }

    return color;
  };

  // Brighten the text color for better contrast
  const getBrightenedTextColor = (color) => {
    if (!color) return "#ffffff";

    let r, g, b;

    // Extract RGB values from color
    if (color.startsWith("#")) {
      r = parseInt(color.slice(1, 3), 16);
      g = parseInt(color.slice(3, 5), 16);
      b = parseInt(color.slice(5, 7), 16);
    } else if (color.startsWith("rgb")) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        [r, g, b] = matches.map(Number);
      } else {
        return color;
      }
    } else {
      return color;
    }

    // Convert to HSL
    const [h, s, l] = rgbToHsl(r, g, b);

    // Increase lightness significantly for better contrast
    // If dark color (l < 50), brighten to 70%
    // If already light (l >= 50), brighten to 80%
    const newL = l < 50 ? 70 : 80;

    // Convert back to RGB
    const [newR, newG, newB] = hslToRgb(h, s, newL);

    return `rgba(${newR}, ${newG}, ${newB}, 1.0)`;
  };

  return (
    <Chip
      label={theLabel.name}
      size="small"
      sx={{
        backgroundColor: convertToRgba(baseColor, 0.2), // Semi-transparent background
        color: getBrightenedTextColor(baseColor), // Brightened text for better contrast
        fontWeight: 600,
        fontSize: "0.75rem",
        borderRadius: "6px",
        border: `1px solid ${convertToRgba(baseColor, 0.3)}`, // Subtle border
      }}
    />
  );
};

export default VideoSchedule;

const parseBGColour = (job, theme) => {
  if (!job.jobNumber)
    return theme === "dark"
      ? "rgba(180, 10, 50, 0.1)"
      : "rgba(180, 10, 50, 0.15)";
  if (
    job.idLabels?.includes("5e6881d6af988c41f2bf6e12") ||
    job.idLabelsPermanent?.includes("5e6881d6af988c41f2bf6e12")
  )
    return theme === "dark"
      ? "rgba(10, 180, 50, 0.1)"
      : "rgba(10, 180, 50, 0.15)";
  return "";
};
